/**
 * What happens to work the last session left behind, when someone signs in.
 *
 * Sign-in starts clean: anything still in the queue belonged to whoever was
 * signed in before, and the server stamps work with the token it arrives
 * under, so sending it after the new sign-in would credit the wrong person.
 * The queue was therefore simply wiped at sign-in.
 *
 * That lost work in one case, found on the tablet 2026-10-08: a tablet that
 * restarts with no signal comes back on the sign-in screen with its held work
 * intact and the last driver's token still stored. The work goes out by
 * itself once the signal returns — but a driver who signs in before it has
 * gone would wipe it.
 *
 * So held work is SENT FIRST (owner decision 2026-10-08: "the offline
 * transactions should be sent before any new ones are sent"), while the last
 * session's token is still the one in hand, and only then does the new
 * sign-in happen. Attribution stays right, and nothing is lost that could
 * have been delivered.
 *
 * When the last log-in has RUN OUT (ten hours) the work is no longer let go
 * (Mark, 2026-10-15): each transaction carries the log-in it was recorded
 * under, so it is KEPT, the new log-in goes ahead, and the work is sent right
 * after it, credited by the server to the driver who did it (types.ts,
 * `recordedSession`). The same when no previous token is stored at all. Only
 * work queued by an older build, which carries no log-in of its own, still
 * cannot be credited to anyone and is let go.
 *
 * Pure: the queue hands in what it can do, so the decision is tested alone.
 */

export type HeldWorkOutcome =
  /**
   * Go on and log in. `kept` is work that will be sent after the log-in,
   * credited to whoever did it; `discarded` is work that cannot be credited to
   * anyone and will be cleared.
   */
  | { proceed: true, kept: number, discarded: number }
  /** Not yet: work is still on its way. Logging in now would put it behind the new work. */
  | { proceed: false, pending: number, reason: string }

export type HeldWorkDeps = {
  /** How many offline transactions are waiting to be sent. */
  pending: () => Promise<number>
  /** How many of those carry no log-in of their own (queued by an older build). */
  unattributed: () => Promise<number>
  /** Whether the last session's token is still stored, to send under. */
  hasPreviousSession: () => boolean
  /** Whether the API can be reached right now. */
  online: () => Promise<boolean>
  /** Send everything that will go. */
  drain: () => Promise<{ drained: boolean, pending: number }>
  /** Whether the server has stopped accepting the last session's token. */
  sessionExpired: () => Promise<boolean>
}

export async function sendHeldWorkFirst(deps: HeldWorkDeps): Promise<HeldWorkOutcome> {
  const waiting = await deps.pending()
  if (waiting === 0) return { proceed: true, kept: 0, discarded: 0 }

  let left = waiting
  if (deps.hasPreviousSession()) {
    if (!await deps.online()) {
      return {
        proceed: false,
        pending: waiting,
        reason: `No connection. ${count(waiting)} ${waiting > 1 ? 'are' : 'is'} waiting to be sent — move into signal and log in again.`,
      }
    }

    const result = await deps.drain()
    if (result.drained) return { proceed: true, kept: 0, discarded: 0 }

    if (!await deps.sessionExpired()) {
      return {
        proceed: false,
        pending: result.pending,
        reason: `${count(result.pending)} ${result.pending > 1 ? 'are' : 'is'} still being sent. Stay in signal and log in again in a moment.`,
      }
    }
    // The last log-in has run out: what is left goes right after the new one.
    left = result.pending
  }

  const unattributed = Math.min(await deps.unattributed(), left)
  return { proceed: true, kept: left - unattributed, discarded: unattributed }
}

function count(n: number): string {
  // "Offline transactions" is the dock's name for work held on the tablet
  // (owner, 2026-10-09) — not "actions".
  return `${n} offline transaction${n > 1 ? 's' : ''}`
}
